import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Loader2, RefreshCw, CheckCircle, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TEMPLATES = [
  {
    key: "trial",
    path: "trial-template",
    title: "Trial confirmation",
    blurb: "Sent when you schedule a trial, with venue, directions and parking.",
    editAt: { label: "Trials page", to: "/admin/trials" },
  },
  {
    key: "training",
    path: "training-template",
    title: "Training attendance",
    blurb: "Day-before reminder with Yes / No buttons that records attendance.",
    editAt: { label: "Reminders tab", tab: "reminders" },
  },
  {
    key: "league",
    path: "league-reminders/template",
    title: "League game reminder",
    blurb: "Fixture reminder days before a JSSL or SYL game.",
    editAt: { label: "Game Reminders tab", tab: "games" },
  },
  {
    key: "cancellation",
    path: "cancellation-template",
    title: "Rain / haze cancellation",
    blurb: "Tells every family booked that day, trialists included.",
    editAt: { label: "Reminders tab", tab: "reminders" },
  },
  {
    key: "survey",
    path: "survey-template",
    title: "Post-trial survey",
    blurb: "Asks the parent how the trial went, with their survey link.",
    editAt: { label: "Trials page", to: "/admin/trials" },
  },
];

const tone = (status) =>
  status === "approved"
    ? "bg-emerald-500/20 text-emerald-300"
    : status === "rejected"
      ? "bg-red-500/20 text-red-300"
      : status
        ? "bg-amber-500/20 text-amber-300"
        : "bg-slate-600/30 text-slate-300";

export const TemplateHub = ({ onGoToTab }) => {
  const [rows, setRows] = useState({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const next = {};
    await Promise.all(
      TEMPLATES.map(async (t) => {
        try {
          const r = await axios.get(`${API}/admin/whatsapp/${t.path}`, { withCredentials: true });
          next[t.key] = r.data;
        } catch (e) {
          next[t.key] = { error: e.response?.data?.detail || "Could not load" };
        }
      })
    );
    setRows(next);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const register = async (t) => {
    setBusy(t.key);
    try {
      const r = await axios.post(`${API}/admin/whatsapp/${t.path}`, {}, { withCredentials: true });
      toast.success(
        r.data.adopted
          ? `${t.title}: picked up the one already approved on your Twilio account`
          : r.data.already
            ? `${t.title} is already registered`
            : `${t.title} submitted to WhatsApp`
      );
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Submission failed");
    } finally {
      setBusy(null);
    }
  };

  return (
    <Card className="p-5 bg-slate-900/50" data-testid="template-hub">
      <div className="flex items-start justify-between mb-4 gap-3 flex-wrap">
        <div>
          <h3 className="text-lg font-semibold text-white">WhatsApp templates</h3>
          <p className="text-xs text-slate-400 mt-0.5 max-w-2xl">
            WhatsApp only lets a business start a conversation with wording it has approved, so each
            message the portal sends out of the blue needs a template. Register once here; edit the
            wording where the message is configured.
          </p>
        </div>
        <Button size="sm" variant="outline" className="border-slate-700 text-slate-300"
          onClick={load} disabled={loading} data-testid="template-hub-refresh">
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />}
          Check status
        </Button>
      </div>

      <div className="space-y-2">
        {TEMPLATES.map((t) => {
          const row = rows[t.key] || {};
          const status = row.status || (row.registered ? "pending" : "");
          return (
            <div
              key={t.key}
              className="p-3 rounded border border-slate-800 bg-slate-950/50 flex items-start justify-between gap-3 flex-wrap"
              data-testid={`template-row-${t.key}`}
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-semibold text-white">{t.title}</span>
                  <Badge className={`text-[10px] ${tone(status)}`} data-testid={`template-status-${t.key}`}>
                    {status || "not registered"}
                  </Badge>
                  {row.error && <span className="text-[11px] text-red-300">{row.error}</span>}
                </div>
                <p className="text-xs text-slate-400 mt-0.5">{t.blurb}</p>
                {row.rejection_reason && (
                  <p className="text-[11px] text-red-300 mt-1">WhatsApp said: {row.rejection_reason}</p>
                )}
                <p className="text-[11px] text-slate-500 mt-1">
                  Wording lives in{" "}
                  {t.editAt.to ? (
                    <Link to={t.editAt.to} className="text-emerald-400 hover:underline inline-flex items-center gap-0.5">
                      {t.editAt.label} <ExternalLink className="w-3 h-3" />
                    </Link>
                  ) : (
                    <button
                      type="button"
                      className="text-emerald-400 hover:underline"
                      onClick={() => onGoToTab?.(t.editAt.tab)}
                      data-testid={`template-goto-${t.key}`}
                    >
                      {t.editAt.label}
                    </button>
                  )}
                </p>
              </div>
              {status !== "approved" && (
                <Button
                  size="sm"
                  onClick={() => register(t)}
                  disabled={busy === t.key}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid={`template-register-${t.key}`}
                >
                  {busy === t.key
                    ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />
                    : <CheckCircle className="w-3.5 h-3.5 mr-1" />}
                  Register
                </Button>
              )}
            </div>
          );
        })}
      </div>
    </Card>
  );
};
